import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { ApiResponse, PagedResponse, SearchParams } from './types';

// --- Pipeline Document (matches backend PipelineDocument : BaseDocument) ---

export interface Pipeline {
  id: string;
  tenant_id: string;
  name: string;
  description: string;
  is_default: boolean;
  status: string;
  branch_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Pipeline Stage Document (matches backend PipelineStageDocument : BaseDocument) ---

export interface PipelineStage {
  id: string;
  tenant_id: string;
  name: string;
  pipeline_id: string;
  sort_order: number;
  probability: number;
  is_won: boolean;
  is_lost: boolean;
  branch_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Request DTOs ---

export interface CreatePipelineRequest {
  name: string;
  description?: string;
  is_default?: boolean;
}

export interface UpdatePipelineRequest {
  name: string;
  description?: string;
  is_default?: boolean;
  status?: string;
}

export interface CreatePipelineStageRequest {
  name: string;
  pipeline_id: string;
  sort_order?: number;
  probability?: number;
  is_won?: boolean;
  is_lost?: boolean;
}

export interface UpdatePipelineStageRequest {
  name: string;
  sort_order?: number;
  probability?: number;
  is_won?: boolean;
  is_lost?: boolean;
}

// --- Filter params ---

export interface PipelineFilters {
  status?: string;
}

// --- Pipeline API functions ---

const BASE = '/pipelines';

export async function listPipelines(
  params: SearchParams,
  filters?: PipelineFilters
): Promise<PagedResponse<Pipeline>> {
  const query: Record<string, unknown> = {
    from: params.from ?? 0,
    size: params.size ?? 20,
  };
  if (params.keyword) query.keyword = params.keyword;
  if (params.sort_field) query.sort_field = params.sort_field;
  if (params.sort_descending !== undefined) query.sort_descending = params.sort_descending;
  if (filters?.status) query.status = filters.status;

  const response = await apiGet<PagedResponse<Pipeline>>(BASE, query);
  if (!response.data) {
    return { items: [], total: 0, page: 1, page_size: 20, total_pages: 0 };
  }
  return response.data;
}

export async function getPipeline(id: string): Promise<Pipeline> {
  const response = await apiGet<Pipeline>(`${BASE}/${id}`);
  if (!response.data) {
    throw new Error(response.message || 'Pipeline not found');
  }
  return response.data;
}

export async function createPipeline(data: CreatePipelineRequest): Promise<Pipeline> {
  const response = await apiPost<Pipeline>(BASE, data);
  if (!response.data) {
    throw new Error(response.message || 'Failed to create pipeline');
  }
  return response.data;
}

export async function updatePipeline(id: string, data: UpdatePipelineRequest): Promise<Pipeline> {
  const response = await apiPut<Pipeline>(`${BASE}/${id}`, data);
  if (!response.data) {
    throw new Error(response.message || 'Failed to update pipeline');
  }
  return response.data;
}

export async function deletePipeline(id: string): Promise<ApiResponse<{ id: string }>> {
  return apiDelete<{ id: string }>(`${BASE}/${id}`);
}

// --- Pipeline Stage API functions ---

export async function listStages(
  pipelineId: string,
  from = 0,
  size = 100
): Promise<PagedResponse<PipelineStage>> {
  const query: Record<string, unknown> = { from, size };
  const response = await apiGet<PagedResponse<PipelineStage>>(
    `${BASE}/${pipelineId}/stages`,
    query
  );
  if (!response.data) {
    return { items: [], total: 0, page: 1, page_size: size, total_pages: 0 };
  }
  return response.data;
}

export async function getStage(id: string): Promise<PipelineStage> {
  const response = await apiGet<PipelineStage>(`${BASE}/stages/${id}`);
  if (!response.data) {
    throw new Error(response.message || 'Stage not found');
  }
  return response.data;
}

export async function createStage(data: CreatePipelineStageRequest): Promise<PipelineStage> {
  const response = await apiPost<PipelineStage>(`${BASE}/stages`, data);
  if (!response.data) {
    throw new Error(response.message || 'Failed to create stage');
  }
  return response.data;
}

export async function updateStage(id: string, data: UpdatePipelineStageRequest): Promise<PipelineStage> {
  const response = await apiPut<PipelineStage>(`${BASE}/stages/${id}`, data);
  if (!response.data) {
    throw new Error(response.message || 'Failed to update stage');
  }
  return response.data;
}

export async function deleteStage(id: string): Promise<ApiResponse<{ id: string }>> {
  return apiDelete<{ id: string }>(`${BASE}/stages/${id}`);
}
